import { PriceSchema } from '@features/state';
import { OrderVoucherSchema } from 'shared-types/Order.schema';
import { z } from 'zod';

export const OrderItemSchema = z.object({
  id: z.number(),
  type: z.string(),
  check_in: z.string().nullable(),
  check_out: z.string().nullable(),
  adults: z.number().nullable(),
  children: z.number().nullable(),
  guest_details: z.array(
    z.object({
      id: z.number(),
      extras: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
          price: z
            .object({
              prefix: z.string(),
              amount: z.number(),
            })
            .nullable(),
          quantity: z.number().nullable().default(1),
        })
      ),
    })
  ),
  selection_groups: z
    .array(
      z.object({
        is_extras: z.boolean(),
        selections: z.array(
          z.object({
            name: z.string(),
            quantity: z.number().optional().default(1),
            price: z
              .object({
                currency_code: z.string(),
                amount: z.number(),
              })
              .nullable(),
          })
        ),
      })
    )
    .optional(),
  dynamic_package: z.literal('parent').or(z.literal('child')).nullable(),
  package_order_item_id: z.number().nullable(),
  bookable: z
    .object({
      name: z.string(),
      room_extras: z.array(
        z
          .object({
            name: z.string(),
            quantity: z.number(),
            price: z
              .object({
                currency_code: z.string(),
                amount: z.number(),
              })
              .nullable(),
          })
          .or(z.string())
      ),
      cancellation_policy: z
        .object({
          policyType: z.string(),
          name: z.string().nullable(),
          description: z.string().nullable(),
          deadline: z
            .object({
              deadlineDate: z.string(),
              hoursBeforeArrival: z.number(),
              afterDeadlineType: z.string(),
              penaltyAfterDeadline: z
                .object({
                  type: z.string(),
                  amount: z.string().or(z.number()),
                })
                .nullable(),
            })
            .nullable(),
        })
        .nullable()
        .optional(),
      rate: z.string().nullable(),
      price: z
        .object({
          prefix: z.string(),
          amount: z.number(),
        })
        .nullable(),
      ids: z
        .object({
          restaurant_name: z.string().optional(),
        })
        .optional()
        .nullable(),
    })
    .nullable(),
  cancellation_details: z
    .object({
      is_cancellable: z.boolean(),
    })
    .optional(),
  status: z.string().nullable(),
});

export const OrderSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  order_reference: z.string().optional(),
  created_at: z.string().optional(),
  total: PriceSchema.optional(),
  order_status: z
    .enum([
      'complete',
      'cancelled',
      'dispatched',
      'new',
      'pending',
      'processing',
      'partially_booked',
      'in_progress',
    ])
    .optional(),
  is_deferred: z.boolean().optional(),
  items: z.array(OrderItemSchema),
  vouchers: z.array(OrderVoucherSchema).optional(),
});

export type OrderType = z.infer<typeof OrderSchema>;
export type OrderItemType = z.infer<typeof OrderItemSchema>;
